import { Action } from 'redux';
import * as Actions from './actionTypes';
import { Quotation, QuotationPdfRequest, QuotationTemplate } from 'src/common/models/quotation';
import { buildErrorAction, ErrorAction } from 'src/common/apiLoader/state/actions';

export interface UpdateQuotationAction extends Action {
    quotationFields: Partial<Quotation>;
}
export interface GetQuotationFileAction extends Action {
    html: string;
}
export interface GetQuotationFileSuccessAction extends Action {
    result: any;
}
export interface UpsertQuotationAction extends Action {
    quotation: Partial<Quotation>;
}
export interface UpsertQuotationSuccessAction extends Action {
    quotation: Quotation;
}
export interface GetQuotationsAction extends Action {
    filters?: any;
}
export interface GetQuotationsSuccessAction extends Action {
    quotations: Quotation[];
}
export interface GetQuotationByIdAction extends Action {
    quotationId: number;
}
export interface GetQuotationByIdSuccessAction extends Action {
    quotation: Quotation;
}
export interface GetQuotationTemplatesSuccessAction extends Action {
    templates: QuotationTemplate[];
}
export interface GetQuotationTemplatesAction extends Action {
    activeOnly: boolean;
}
export interface UpsertQuotationTemplateAction extends Action {
    template: Partial<QuotationTemplate>;
}
export interface UpsertQuotationTemplateSuccessAction extends Action {
    template: QuotationTemplate;
}
export interface GenerateQuotationPdfAction extends Action {
    quotationId: number;
    request: QuotationPdfRequest;
}
export interface GenerateQuotationPdfSuccessAction extends Action {
    result: any;
}
export interface SendQuotationAction extends Action {
    quotationId: number;
    request: QuotationPdfRequest;
}
export interface SendQuotationSuccessAction extends Action {
    success: boolean;
}
export interface UpdateQuotationStatusAction extends Action {
    quotationId: number;
    status: string;
}
export interface UpdateQuotationStatusSuccessAction extends Action {
    success: boolean;
}

export const updateCurrentQuotation = (quotationFields: Partial<Quotation>): UpdateQuotationAction => {
    return {
        type: Actions.UPDATE_CURRENT_QUOTATION,
        quotationFields,
    }
};

export const cleanCurrentQuotation = (): Action => {
    return {
        type: Actions.CLEAN_CURRENT_QUOTATION,
    }
};

export const cleanQuotationFile = (): Action => {
    return {
        type: Actions.CLEAN_QUOTATION_FILE,
    }
};

export const getQuotationFileApi = (html: string): GetQuotationFileAction => {
    return {
        type: Actions.GET_QUOTATION_FILE_API,
        html,
    }
};
export const geQuotationFileApiSuccess = (result: any): GetQuotationFileSuccessAction => ({
    type: Actions.GET_QUOTATION_FILE_API_SUCCESS,
    result,
})
export const getQuotationFileApiError = (error?: any): ErrorAction => buildErrorAction(
    Actions.GET_QUOTATION_FILE_API_ERROR,
    error,
)

export const upsertQuotationApi = (quotation: Partial<Quotation>): UpsertQuotationAction => ({
    type: Actions.UPSERT_QUOTATION_API,
    quotation,
})
export const upsertQuotationApiSuccess = (quotation: Quotation): UpsertQuotationSuccessAction => ({
    type: Actions.UPSERT_QUOTATION_API_SUCCESS,
    quotation,
})
export const upsertQuotationApiError = (error?: any): ErrorAction => buildErrorAction(
    Actions.UPSERT_QUOTATION_API_ERROR,
    error,
)

export const getQuotationsApi = (filters?: any): GetQuotationsAction => ({
    type: Actions.GET_QUOTATIONS_API,
    filters,
})
export const getQuotationsApiSuccess = (quotations: Quotation[]): GetQuotationsSuccessAction => ({
    type: Actions.GET_QUOTATIONS_API_SUCCESS,
    quotations,
})
export const getQuotationsApiError = (error?: any): ErrorAction => buildErrorAction(
    Actions.GET_QUOTATIONS_API_ERROR,
    error,
)

export const getQuotationByIdApi = (quotationId: number): GetQuotationByIdAction => ({
    type: Actions.GET_QUOTATION_BY_ID_API,
    quotationId,
})
export const getQuotationByIdApiSuccess = (quotation: Quotation): GetQuotationByIdSuccessAction => ({
    type: Actions.GET_QUOTATION_BY_ID_API_SUCCESS,
    quotation,
})
export const getQuotationByIdApiError = (error?: any): ErrorAction => buildErrorAction(
    Actions.GET_QUOTATION_BY_ID_API_ERROR,
    error,
)

export const getQuotationTemplatesApi = (activeOnly: boolean = true): GetQuotationTemplatesAction => ({
    type: Actions.GET_QUOTATION_TEMPLATES_API,
    activeOnly,
})
export const getQuotationTemplatesApiSuccess = (templates: QuotationTemplate[]): GetQuotationTemplatesSuccessAction => ({
    type: Actions.GET_QUOTATION_TEMPLATES_API_SUCCESS,
    templates,
})
export const getQuotationTemplatesApiError = (error?: any): ErrorAction => buildErrorAction(
    Actions.GET_QUOTATION_TEMPLATES_API_ERROR,
    error,
)

export const upsertQuotationTemplateApi = (template: Partial<QuotationTemplate>): UpsertQuotationTemplateAction => ({
    type: Actions.UPSERT_QUOTATION_TEMPLATE_API,
    template,
})
export const upsertQuotationTemplateApiSuccess = (template: QuotationTemplate): UpsertQuotationTemplateSuccessAction => ({
    type: Actions.UPSERT_QUOTATION_TEMPLATE_API_SUCCESS,
    template,
})
export const upsertQuotationTemplateApiError = (error?: any): ErrorAction => buildErrorAction(
    Actions.UPSERT_QUOTATION_TEMPLATE_API_ERROR,
    error,
)

export const generateQuotationPdfApi = (quotationId: number, request: QuotationPdfRequest): GenerateQuotationPdfAction => ({
    type: Actions.GENERATE_QUOTATION_PDF_API,
    quotationId,
    request,
})
export const generateQuotationPdfApiSuccess = (result: any): GenerateQuotationPdfSuccessAction => ({
    type: Actions.GENERATE_QUOTATION_PDF_API_SUCCESS,
    result,
})
export const generateQuotationPdfApiError = (error?: any): ErrorAction => buildErrorAction(
    Actions.GENERATE_QUOTATION_PDF_API_ERROR,
    error,
)

export const sendQuotationApi = (quotationId: number, request: QuotationPdfRequest): SendQuotationAction => ({
    type: Actions.SEND_QUOTATION_API,
    quotationId,
    request,
})
export const sendQuotationApiSuccess = (success: boolean): SendQuotationSuccessAction => ({
    type: Actions.SEND_QUOTATION_API_SUCCESS,
    success,
})
export const sendQuotationApiError = (error?: any): ErrorAction => buildErrorAction(
    Actions.SEND_QUOTATION_API_ERROR,
    error,
)

export const updateQuotationStatusApi = (quotationId: number, status: string): UpdateQuotationStatusAction => ({
    type: Actions.UPDATE_QUOTATION_STATUS_API,
    quotationId,
    status,
})
export const updateQuotationStatusApiSuccess = (success: boolean): UpdateQuotationStatusSuccessAction => ({
    type: Actions.UPDATE_QUOTATION_STATUS_API_SUCCESS,
    success,
})
export const updateQuotationStatusApiError = (error?: any): ErrorAction => buildErrorAction(
    Actions.UPDATE_QUOTATION_STATUS_API_ERROR,
    error,
)
